import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, ClipboardList, Compass, Handshake, Users, TrendingUp, ArrowRight, Award, ShieldCheck, Sparkles, MapPin, CalendarClock, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import Reveal from "@/components/Reveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import VisitRequestModal from "@/components/VisitRequestModal";
import { HOME } from "@/constants/testIds";
import useSEO from "@/hooks/useSEO";

const iconMap = { Building2, ClipboardList, Compass, Handshake, Users, TrendingUp };

export default function Home() {
  const { t } = useTranslation();
  const { company, contact } = useSite();
  useSEO({
    title: "Promoteur immobilier haut de gamme à Oran",
    description: "AMAR SAHRAOUI : promotion immobilière de prestige à Oran, Algérie. Résidences premium, projets en cours, logements disponibles à la réservation. La Résidence ~THE GARDEN~ et nos programmes neufs.",
    path: "/",
  });
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [visitOpen, setVisitOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/public/services").then((r) => r.data).catch(() => []),
      api.get("/public/projects").then((r) => r.data).catch(() => []),
      api.get("/public/availabilities", { params: { status: "disponible" } }).then((r) => r.data).catch(() => []),
      api.get("/public/testimonials").then((r) => r.data).catch(() => []),
    ]).then(([s, p, a, ts]) => {
      setServices(s);
      setProjects(p);
      setAvailabilities(a);
      setTestimonials(ts);
    });
  }, []);

  const featuredProjects = projects.filter((p) => p.featured);
  const ongoingProjects = projects.filter((p) => p.status === "en_cours").slice(0, 3);

  return (
    <div>
      {/* HERO - 50/50 divide blueprint */}
      <section className="relative grid grid-cols-1 lg:grid-cols-2 min-h-[88vh]">
        {/* Left: text on dark surface */}
        <div className="bg-atelier-deep px-5 md:px-12 lg:px-16 py-20 lg:py-28 flex flex-col justify-center relative overflow-hidden">
          <div className="absolute inset-0 blueprint-grid opacity-30" aria-hidden="true" />
          <div className="relative z-10 max-w-xl">
            <Reveal>
              <span className="label-tech text-atelier-ink tracking-[0.3em] block mb-6">
                ORAN — ALGÉRIE · PROMOTION IMMOBILIÈRE
              </span>
              <h1 className="font-serif text-[2.5rem] sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight text-atelier-text mb-8">
                {t("hero.title1")}
                <br />
                <span className="italic text-atelier-ink">{t("hero.title2")}</span>
              </h1>
              <p className="text-atelier-textDim text-base lg:text-lg max-w-md leading-relaxed mb-10">
                {company?.slogan || "Votre chez-soi vous attend, conçu avec soin, bâti pour votre sérénité."}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/projets" className="btn-primary" data-testid={HOME.heroCta}>
                  {t("cta.discoverProjects")} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <button onClick={() => setVisitOpen(true)} className="btn-outline" data-testid="home-hero-visit-cta">
                  <CalendarClock className="w-3.5 h-3.5" /> {t("cta.bookVisit")}
                </button>
              </div>
            </Reveal>
          </div>
          {/* Technical annotation bottom-right */}
          <div className="absolute bottom-8 right-8 hidden md:flex items-center gap-3">
            <div className="w-12 h-px bg-atelier-ink/30" />
            <span className="label-tech text-[0.55rem] text-atelier-ink/50">RÉF · BLUEPRINT 24.07</span>
          </div>
        </div>

        {/* Right: hero image with blueprint frame */}
        <div className="relative bg-atelier-surface overflow-hidden flex items-center justify-center p-6 lg:p-10">
          <div className="absolute inset-0 blueprint-grid opacity-10" aria-hidden="true" />
          <div className="relative w-full h-full min-h-[400px] tech-corners proj-img-shell">
            <img
              src={company?.hero_image_url || "https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=2400&q=80"}
              alt="Architecture haut de gamme"
              className="w-full h-full object-cover opacity-75"
            />
            <div className="absolute top-4 left-4 atelier-border bg-atelier-deep/80 px-3 py-1.5 backdrop-blur-sm">
              <span className="label-tech text-[0.55rem] text-atelier-ink">PROJET PHARE</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="py-20 lg:py-28 bg-atelier border-t atelier-border-soft relative">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <div className="label-tech text-atelier-ink mb-5">— Présentation</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tight text-atelier-text">
                Un promoteur immobilier <span className="italic text-atelier-ink">d'exception</span> à Oran.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-7">
            <Reveal delay={150}>
              <p className="text-lg leading-relaxed text-atelier-textDim mb-6">
                {company?.short_intro}
              </p>
              {company?.long_about && (
                <p className="text-base leading-relaxed text-atelier-textDim/80 mb-8">
                  {company.long_about.split(". ").slice(0, 2).join(". ") + "."}
                </p>
              )}
              <Link to="/societe" className="inline-flex items-center gap-2 label-tech text-atelier-ink border-b border-atelier-ink/40 pb-1 hover:border-atelier-ink transition">
                Découvrir la société <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* KEY FIGURES */}
      {company?.key_figures?.length > 0 && (
        <section className="bg-atelier-deep border-y atelier-border-soft relative">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {company.key_figures.map((kf, i) => (
              <div
                key={i}
                className={`py-12 lg:py-16 px-5 lg:px-10 ${i < company.key_figures.length - 1 ? "lg:border-r" : ""} atelier-border-soft ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""}`}
              >
                <div className="font-serif text-5xl lg:text-7xl text-atelier-ink leading-none mb-3">
                  <AnimatedCounter value={kf.value} />
                </div>
                <div className="label-tech text-atelier-textDim">{kf.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES — Expertise grid */}
      <section className="py-20 lg:py-28 bg-atelier border-t atelier-border-soft relative">
        <div className="px-5 lg:px-10">
          <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <div className="label-tech text-atelier-ink mb-4">— Nos prestations</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-atelier-text max-w-2xl">
                Un savoir-faire intégré, <span className="italic text-atelier-ink">de A à Z.</span>
              </h2>
            </Reveal>
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-atelier-ink animate-pulse-soft" />
              <Link to="/prestations" className="label-tech text-atelier-textDim hover:text-atelier-ink transition-colors flex items-center gap-2" data-testid={HOME.servicesLink}>
                Toutes les prestations <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 atelier-border">
            {services.slice(0, 6).map((s, idx) => {
              const Icon = iconMap[s.icon] || Building2;
              const isLast = idx === Math.min(services.length, 6) - 1;
              return (
                <Link
                  key={s.id}
                  to={`/prestations/${s.slug}`}
                  className={`scan-card p-8 lg:p-10 group hover:bg-atelier-surface transition-all duration-500 ${!isLast ? "border-b md:border-b-0 md:border-r atelier-border-soft" : ""}`}
                  data-testid={`service-card-${s.slug}`}
                >
                  <Icon className="w-10 h-10 text-atelier-ink mb-7 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.3} />
                  <h3 className="label-tech text-base font-medium text-atelier-text mb-3 tracking-widest uppercase">{s.title}</h3>
                  <p className="text-sm text-atelier-textDim leading-relaxed mb-7 font-serif">{s.short_description}</p>
                  <div className="label-tech text-atelier-ink flex items-center gap-2 group-hover:gap-3 transition-all">
                    <Plus className="w-3 h-3" /> Découvrir
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      {featuredProjects.length > 0 && (
        <section className="py-20 lg:py-28 bg-atelier-deep relative">
          <div className="px-5 lg:px-10">
            <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <Reveal>
                <div className="label-tech text-atelier-ink mb-4">— Projets à la une</div>
                <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-atelier-text max-w-2xl">
                  Nos signatures <span className="italic text-atelier-ink">architecturales.</span>
                </h2>
              </Reveal>
              <Link to="/projets" className="label-tech text-atelier-textDim hover:text-atelier-ink transition-colors flex items-center gap-2" data-testid={HOME.projectsLink}>
                Tous les projets <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {featuredProjects.slice(0, 2).map((p, idx) => (
                <Link
                  key={p.id}
                  to={`/projets/${p.slug}`}
                  className={`group block proj-img-shell tech-corners relative atelier-border ${idx === 0 ? "lg:col-span-7" : "lg:col-span-5"}`}
                  data-testid={`project-featured-${p.slug}`}
                >
                  <div className={`relative overflow-hidden bg-atelier-surface ${idx === 0 ? "aspect-[4/5] lg:aspect-[5/6]" : "aspect-[4/5] lg:aspect-[5/6]"}`}>
                    <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                    <div className="absolute inset-0 bg-gradient-to-t from-atelier-deep via-atelier-deep/30 to-transparent" />
                    <div className="absolute top-6 left-6 atelier-border bg-atelier-deep/80 backdrop-blur-sm px-3 py-1.5">
                      <span className="label-tech text-[0.6rem] text-atelier-ink">{statusLabel(p.status)}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-10">
                      <div className="label-tech text-atelier-ink mb-2 flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> {p.city}, {p.wilaya}
                      </div>
                      <h3 className="font-serif text-2xl lg:text-4xl leading-tight mb-3 text-atelier-text group-hover:text-atelier-ink transition-colors">{p.name}</h3>
                      <p className="text-sm text-atelier-textDim max-w-xl mb-4 line-clamp-2 font-serif">{p.short_description}</p>
                      <div className="inline-flex items-center gap-2 label-tech text-atelier-text group-hover:gap-3 transition-all">
                        Voir le projet <ArrowUpRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MARQUEE */}
      <div className="w-full bg-atelier-surfaceMid border-y atelier-border-soft py-4 overflow-hidden whitespace-nowrap relative z-10">
        <div className="flex animate-marquee gap-16">
          <span className="label-tech text-[0.66rem] text-atelier-ink tracking-[0.4em]">QUALITÉ D'EXÉCUTION • EMPLACEMENTS STRATÉGIQUES • VISION DURABLE • TRANSPARENCE • ORAN ALGÉRIE •</span>
          <span className="label-tech text-[0.66rem] text-atelier-ink tracking-[0.4em]">QUALITÉ D'EXÉCUTION • EMPLACEMENTS STRATÉGIQUES • VISION DURABLE • TRANSPARENCE • ORAN ALGÉRIE •</span>
        </div>
      </div>

      {/* ONGOING PROJECTS */}
      {ongoingProjects.length > 0 && (
        <section className="py-20 lg:py-28 bg-atelier relative">
          <div className="px-5 lg:px-10">
            <Reveal>
              <div className="mb-12">
                <div className="label-tech text-atelier-ink mb-4">— Projets en cours</div>
                <h2 className="font-serif text-3xl lg:text-5xl tracking-tight text-atelier-text">Les chantiers en <span className="italic text-atelier-ink">mouvement.</span></h2>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ongoingProjects.map((p) => (
                <Link key={p.id} to={`/projets/${p.slug}`} className="group block atelier-border hover:border-atelier-ink/60 transition-all duration-500 scan-card bg-atelier-surface/40" data-testid={`project-ongoing-${p.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-atelier-surface proj-img-shell">
                    <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="label-tech text-atelier-ink mb-2">{p.city} — Avancement {p.progress_percent}%</div>
                    <h3 className="font-serif text-xl mb-2 text-atelier-text">{p.name}</h3>
                    <p className="text-sm text-atelier-textDim line-clamp-2 font-serif mb-4">{p.short_description}</p>
                    <div className="h-px bg-atelier-line">
                      <div className="h-full bg-atelier-ink transition-all duration-700" style={{ width: `${p.progress_percent}%` }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AVAILABILITIES TEASER */}
      {availabilities.length > 0 && (
        <section className="py-20 bg-atelier-deep border-y atelier-border-soft">
          <div className="px-5 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="label-tech text-atelier-ink mb-4">— Disponibilités</div>
                <h2 className="font-serif text-3xl lg:text-5xl tracking-tight text-atelier-text">Logements à la <span className="italic text-atelier-ink">réservation.</span></h2>
              </div>
              <Link to="/disponibilites" className="btn-outline" data-testid="home-availabilities-link">Voir toutes les disponibilités</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {availabilities.slice(0, 4).map((a) => (
                <div key={a.id} className="atelier-border bg-atelier-surface/40 p-6 hover:border-atelier-ink/50 transition-all duration-400 scan-card" data-testid={`avail-card-${a.reference}`}>
                  {a.image_url && (
                    <div className="aspect-[4/3] mb-4 overflow-hidden bg-atelier-surface proj-img-shell">
                      <img src={a.image_url} alt={a.reference} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="label-tech text-atelier-ink mb-2">{a.project_name}</div>
                  <div className="font-serif text-xl text-atelier-text mb-1">{a.reference}</div>
                  <div className="text-sm text-atelier-textDim mb-3 font-serif">{a.type} — {a.surface} {a.rooms ? `· ${a.rooms} pièces` : ""}</div>
                  <span className={`pill ${a.status === "disponible" ? "text-atelier-ink" : a.status === "reserve" ? "text-amber-400" : "text-atelier-textFaint"}`}>{statusLabelAvail(a.status)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-atelier relative">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <div className="label-tech text-atelier-ink mb-4">— Pourquoi nous choisir</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-[1.05] text-atelier-text">
                Une exigence <span className="italic text-atelier-ink">absolue</span> à chaque étape.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-8">
            {[
              { Icon: Award, title: "Qualité d'exécution", desc: "Matériaux nobles, finitions soignées, partenaires de confiance." },
              { Icon: ShieldCheck, title: "Transparence", desc: "Une communication claire et des engagements respectés." },
              { Icon: Sparkles, title: "Vision durable", desc: "Des conceptions pensées pour traverser les décennies." },
              { Icon: MapPin, title: "Emplacements stratégiques", desc: "Des programmes parfaitement situés à Oran et sa région." },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="border-l atelier-border-soft pl-6">
                <Icon className="w-7 h-7 text-atelier-ink mb-4" strokeWidth={1.3} />
                <h3 className="label-tech text-atelier-text text-sm mb-2">{title}</h3>
                <p className="text-sm text-atelier-textDim leading-relaxed font-serif">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-atelier-deep">
          <div className="px-5 lg:px-10">
            <Reveal>
              <div className="label-tech text-atelier-ink mb-4">— Ils nous ont fait confiance</div>
              <h2 className="font-serif text-3xl lg:text-5xl mb-14 max-w-3xl text-atelier-text">La confiance, notre <span className="italic text-atelier-ink">plus belle</span> récompense.</h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-px bg-atelier-line atelier-border">
              {testimonials.slice(0, 3).map((tm) => (
                <div key={tm.id} className="bg-atelier-deep p-8 lg:p-10">
                  <div className="text-atelier-ink text-4xl font-serif mb-4 leading-none">"</div>
                  <p className="text-atelier-textDim leading-relaxed mb-6 italic font-serif">{tm.content}</p>
                  <div className="label-tech text-atelier-textFaint">{tm.name} · {tm.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT BAND */}
      <section className="py-20 lg:py-28 bg-atelier-surface border-t atelier-border-soft">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="label-tech text-atelier-ink mb-4">— Contact direct</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-atelier-text">Parlons de votre <span className="italic text-atelier-ink">projet immobilier.</span></h2>
            <p className="mt-6 max-w-xl text-atelier-textDim leading-relaxed font-serif">Nos chargés de clientèle répondent à toutes vos questions et organisent une visite des projets sur simple demande.</p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            {contact?.phone && (
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="flex items-center justify-between atelier-border px-6 py-4 hover:bg-atelier-ink hover:border-atelier-ink hover:text-atelier-deep transition-all duration-400 group">
                <span className="label-tech">Téléphone</span>
                <span className="font-serif text-lg">{contact.phone}</span>
              </a>
            )}
            {contact?.whatsapp && (
              <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center justify-between atelier-border px-6 py-4 hover:bg-atelier-ink hover:border-atelier-ink hover:text-atelier-deep transition-all duration-400">
                <span className="label-tech">WhatsApp</span>
                <span className="font-serif text-lg">{contact.whatsapp}</span>
              </a>
            )}
            <Link to="/contact" className="mt-2 btn-primary justify-center" data-testid="home-contact-cta">
              Demander un rappel <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <VisitRequestModal open={visitOpen} onClose={() => setVisitOpen(false)} project={null} />
    </div>
  );
}

function statusLabel(s) {
  return s === "realise" ? "Réalisé" : s === "en_cours" ? "En cours" : s === "futur" ? "À venir" : s;
}
function statusLabelAvail(s) {
  return s === "disponible" ? "Disponible" : s === "reserve" ? "Réservé" : s === "vendu" ? "Vendu" : s;
}
